import React, { useEffect, useMemo, useState } from 'react';
import AddressForm from '../AddressForm/AddressForm';
import RequestOtherDeliveryDates from './RequestOtherDeliveryDates';
import styles from './AddressConfirmation.module.scss';
import { Button } from '@ds';
import { getMACAddress, confirmMAC, setMACAddressConfirmed, updateMACNotes } from 'src/shared/services/fn-api/MACService';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { Address } from 'src/shared/types/Address';
import { ButtonSize } from '@ds/elements/Button/Button.types';
import { addBusinessDays, getUsDateString } from '../../../utils/dateHelpers';
import { RequestedDeliveryDateRange } from '../../../utils/constants/delivery';
import checkCircle from '@icons/checkmark-circle.svg';
import { useQueryClient } from '@tanstack/react-query';
import { AUTOMATION_PENDING_CONFIRMATION } from '../../../utils/constants/queryKeys';

type AddressUpdateModel = Address & {
  shouldUpdateCurrentResidence?: boolean;
  shouldUpdatePermanentResidence?: boolean;
  addressType?: string;
  contactAddressId?: number;
};

type RequestedDeliveryDates = {
  from: Date | null;
  to: Date | null;
};



const AddressConfirmation: React.FC = () => {
  const [currentAddress, setCurrentAddress] = useState<Address | null>();
  const {data:user} = useGetUser();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [successMsg, setSuccessMsg] = useState('Mailing address confirmed');

  // Delivery date states
  const [isSucceedToConfirmDeliveryDate, setConfirmDeliveryDateStatus] = useState(false);
  const [caseIds, setCaseIds] = useState<number[]>([]);
  const [shouldShowCalendar, setShowCalendarStatus] = useState<boolean>(false);
  const [deliveryFromDate, setDeliveryFromDate] = useState<Date>();
  const [deliveryToDate, setDeliveryToDate] = useState<Date>();
  const [requestedDate, setRequestedDate] = useState<RequestedDeliveryDates>({
    from: null,
    to: null,
  });

  const userId = user?.userId;

  useEffect(() => {
    if (!userId) return;

    const fetchAddress = async () => {
      setIsLoading(true);
      try {
        const contactAddress: Address = await getMACAddress(userId);
        if (contactAddress) {
          setCurrentAddress(contactAddress);
        } else {
          setCurrentAddress(null);
        }
        
      } catch (error) {
        console.error('Error fetching MAC address:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAddress();
  }, [userId]);

  const getAddressView = (address: Address) => {
    const firstLine = [address.addressLine1, address.suite]
      .filter((x) => !!x)
      .join(', ');

    const secondLine = [address.addressLine2]
      .filter((x) => !!x)
      .join(', ');

    const thirdLine = [
      address.city,
      address.state,
      address.zipCode,
      address.country,
    ]
      .filter((x) => !!x)
      .join(', ');

    return [firstLine, secondLine, thirdLine];
  };

  const addressLine = useMemo(() => {
    if (!currentAddress) return;

    const addressView = getAddressView(currentAddress);
    return addressView
      .filter((x) => !!x)
      .map((address, index) => <p key={index}>{address}</p>);
  }, [currentAddress]);

  const confirmAddress = async (data: AddressUpdateModel | undefined, isModified = true) => {
    if (!data) return;
    
    setIsLoading(true);
    let addressNotes = 'Confirmed Mailing Address:\n' + confirmAddressNotesForServiceNow(data);
    
    try {
      const returnedCaseIds = await setMACAddressConfirmed({
        beneId: user?.beneId,
        state: 'AddressConfirmationReceived',
        notes: addressNotes,
      });

      setCaseIds(returnedCaseIds);

      await confirmMAC({
        beneId: user?.beneId,
        mailingAddress: data,
        shouldUpdatePermanentResidence: data
          ? data.shouldUpdatePermanentResidence
          : false,
        shouldUpdateCurrentResidence: data
          ? data.shouldUpdateCurrentResidence
          : false,
      });

      setSuccessMsg(
        !isModified
          ? 'Mailing address confirmed'
          : 'Mailing address updated'
      );
      
      // Set delivery dates when address is confirmed
      const currentConfirmDate = new Date();
      setDeliveryFromDate(
        addBusinessDays(
          currentConfirmDate,
          RequestedDeliveryDateRange.Start
        )
      );
      setDeliveryToDate(
        addBusinessDays(currentConfirmDate, RequestedDeliveryDateRange.End)
      );
      
      data && setCurrentAddress(data);
      setIsConfirmed(true);
      
      // Invalidate automation pending confirmation query to hide the banner
      queryClient.invalidateQueries({
        queryKey: [AUTOMATION_PENDING_CONFIRMATION, user?.beneId]
      });
    } catch (error) {
      console.error('Error confirming address:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const showForm = () => {
    setFormVisible(true);
  };

  // Delivery date handlers
  const handleRequestOtherDateClick = () => {
    setShowCalendarStatus(true);
  };

  const handleCloseRequestOtherDate = () => {
    setShowCalendarStatus(false);
  };

  const handleDateRangeChange = (fromDate: Date | null, toDate: Date | null) => {
    setRequestedDate({ from: fromDate, to: toDate });
  };

  const handleSubmitDeliveryDate = async () => {
    if (!requestedDate.from || !requestedDate.to) return;
    
    const deliveryDataFrom = getUsDateString(requestedDate.from);
    const deliveryDataTo = getUsDateString(requestedDate.to);
    const deliveryNotes = `Requested Delivery Dates: (${deliveryDataFrom}) - (${deliveryDataTo})`;

    try {
      await updateMACNotes({
        caseIds: caseIds,
        notes: deliveryNotes,
      });
      
      setConfirmDeliveryDateStatus(true);
      handleCloseRequestOtherDate();
      setDeliveryFromDate(requestedDate.from);
      setDeliveryToDate(requestedDate.to);
    } catch (error) {
      console.error('Error updating delivery date notes:', error);
    }
  };

  const handleConfirmAddressWithoutModifying = () => {
    const address: AddressUpdateModel = {
      ...currentAddress,
      shouldUpdateCurrentResidence: false,
      shouldUpdatePermanentResidence: false,
    };
    confirmAddress(address, false);
  };

  const confirmAddressNotesForServiceNow = (data: Address | undefined) => {
    if (!data) return '';

    const addressView = getAddressView(data);
    return addressView.filter((x) => !!x).join('\n');
  };

  return (
    <div className={styles['address-confirmation']}>
      <h2 className={styles.title}>Confirm or update your mailing address</h2>
      <div className={styles.description}>
        {isConfirmed
          ? 'This address will be used to mail the approval notices for you and your dependents'
          : 'Is the following address correct? This address will be used to mail the approval notices for you and your dependents'}
      </div>
      <div className={styles['mailing-info']}>
        <div className={styles['full-name']}>{user?.fullName}</div>
        <div className={addressLine ? styles['address']: styles['no-address']}>
          {isLoading ? <div /> : addressLine ? addressLine : 'No address on record'}
        </div>
      </div>
      {isConfirmed ? (
        <div className={styles['success-section']}>
          <div className={styles['success-message']}>
            <img src={checkCircle} className={styles['success-icon']} />
            <div className={styles['success-text']}>
              {isSucceedToConfirmDeliveryDate
                ? "You successfully sent a note requesting other dates. " +
                  "The approval notices will be delivered to the above address approximately between that time frame. " +
                  "You will soon receive packet tracking information via email."
                : successMsg}
            </div>
          </div>
          {deliveryFromDate && deliveryToDate && (
            <RequestOtherDeliveryDates
              requestedFromDate={requestedDate.from}
              requestedToDate={requestedDate.to}
              deliveryFromDate={deliveryFromDate}
              deliveryToDate={deliveryToDate}
              shouldShowCalendar={shouldShowCalendar}
              isSucceedToConfirmDeliveryDate={isSucceedToConfirmDeliveryDate}
              onDateRangeChange={handleDateRangeChange}
              onSubmitChangeDeliveryDate={handleSubmitDeliveryDate}
              onCancelRequestOtherDate={handleCloseRequestOtherDate}
              onRequestOtherDateClick={handleRequestOtherDateClick}
            />
          )}
        </div>
      ) : formVisible ? (
        <AddressForm
          currentAddress={currentAddress}
          onSubmit={confirmAddress}
        />
      ) : !isLoading && currentAddress? (
        <div className={styles['actions']}>
          <Button
            onClick={() => handleConfirmAddressWithoutModifying()}
            styleType='secondary'
            variant='outlined'
            disabled={isLoading}
            size={ButtonSize.large}
          >
            Yes, confirm address
          </Button>
          <Button styleType='secondary' variant='outlined' size={ButtonSize.large} onClick={showForm}>
            No, update address
          </Button>
        </div>
      ) : (
        <div><div className={styles['actions']}>
          <Button styleType='secondary' variant='outlined' size={ButtonSize.large} onClick={showForm}>
            Add Address
          </Button>
        </div></div>
      )}
    </div>
  );
};

export default AddressConfirmation;